/**
 * Browser twin of server/utils/campaign-contacts.js — the ONE way client
 * code reads or writes a campaign's guest list.
 *
 * Read that file for the why. The short version: guest lists are leaving the
 * bulk_campaigns document for chunk docs under it
 * (`bulk_campaigns/{id}/contact_chunks/{n}` = `{ index, items[] }`), and until
 * the migration is through both shapes exist side by side. Anything that
 * touches `campaign.contacts` from the browser — the sharing wizard, the host
 * dashboard, the reminders page, the admin dashboard — must go through here
 * or it will see an empty guest list on a migrated campaign.
 *
 * Compat SDK (firebase v8 API): `db` is `window.firebaseDb`.
 */
(function () {
  'use strict';

  const COLLECTION = 'bulk_campaigns';
  const SUBCOLLECTION = 'contact_chunks';
  const STORAGE_FIELD = 'contactsStorage';
  const CHUNKS_FIELD = 'contactChunks';
  // Bumped with every chunked list write (server twin too), so a reader can
  // reuse the list it already holds when only another field changed. See
  // contactsKey() below.
  const REV_FIELD = 'contactsRev';
  const STORAGE_CHUNKS = 'chunks';
  const CHUNK_SIZE = 1000;
  // Bumped by every guest-list write (here and in the server twin), so an
  // editor that loaded version N can tell the list was rewritten under it —
  // a co-host's add-contacts, the dispatcher's detail overlay — instead of
  // saving its stale copy over theirs (pre-mortem #13, 2026-10-07).
  const VERSION_FIELD = 'contactsVersion';
  function versionBump() {
    const fv = typeof firebase !== 'undefined' && firebase.firestore && firebase.firestore.FieldValue;
    return fv && typeof fv.increment === 'function' ? { [VERSION_FIELD]: fv.increment(1) } : {};
  }

  // Phase 2 flips this to 'chunks' in the SAME deploy as the server's
  // CAMPAIGN_CONTACTS_STORAGE env var, so browser and server never disagree
  // about where a freshly written list goes.
  const WRITE_STORAGE = 'chunks';

  function chunksRef(db, campaignId) {
    return db.collection(COLLECTION).doc(campaignId).collection(SUBCOLLECTION);
  }
  function hasInline(data) { return !!data && Array.isArray(data.contacts); }
  function hasChunked(data) { return !!data && data[STORAGE_FIELD] === STORAGE_CHUNKS; }

  /** The guest list for a campaign doc's data, whichever shape it is in. */
  async function load(db, campaignId, data) {
    if (hasInline(data)) return data.contacts;
    if (!hasChunked(data)) return [];
    const snap = await chunksRef(db, campaignId).orderBy('index').get();
    const out = [];
    snap.forEach((d) => {
      const items = d.get('items');
      if (Array.isArray(items)) out.push(...items);
    });
    return out;
  }

  /**
   * What identifies a chunked guest list's version, read from the parent doc
   * alone; null when the doc is not chunked (an inline list rides in the doc).
   * Every chunk write commits in one batch with a new contactsRev, so an equal
   * key means the chunks are the ones already read. contactChunks and
   * totalContacts cover docs written before contactsRev existed: any rewrite
   * since adds the rev, which changes the key.
   */
  function contactsKey(data) {
    if (!hasChunked(data)) return null;
    return [data[CHUNKS_FIELD], data[REV_FIELD], data.totalContacts]
      .map((v) => (v === undefined || v === null ? '' : String(v))).join('|');
  }

  /** True when `prev` (an earlier hydrated row) still holds `data`'s guest list. */
  function canReuse(data, prev) {
    if (!prev || !Array.isArray(prev.contacts)) return false;
    const key = contactsKey(data);
    return key !== null && key === contactsKey(prev);
  }

  /**
   * `snap.data()` with `contacts` filled in; null for a missing doc.
   * `opts.reuse`: the caller's earlier hydrated row for this campaign. When
   * its list is still current (canReuse) it is handed back instead of
   * re-reading every chunk. The dashboard listener fires on every campaign
   * doc write; during a send that is many times a minute, and at 3k+ guests
   * each re-read was about 1 MB per open tab (pre-mortem 2026-10-07).
   */
  async function hydrate(db, snap, opts) {
    if (!snap || !snap.exists) return null;
    const data = snap.data();
    if (hasInline(data)) return data;
    const prev = opts && opts.reuse;
    if (canReuse(data, prev)) return { ...data, contacts: prev.contacts };
    return { ...data, contacts: await load(db, snap.id, data) };
  }

  /**
   * Hydrate a query snapshot's docs in parallel, in order: [{id, ref, data}].
   * `opts.reuseFrom`: earlier hydrated rows ({id, contacts, ...}) to reuse
   * unchanged guest lists from, matched by id.
   */
  function hydrateAll(db, querySnap, opts) {
    const byId = new Map(((opts && opts.reuseFrom) || []).filter(Boolean).map((r) => [r.id, r]));
    return Promise.all(querySnap.docs.map((d) => hydrate(db, d, { reuse: byId.get(d.id) }).then((data) => ({ id: d.id, ref: d.ref, data }))));
  }

  /**
   * A copy of a guest list an editor may change freely: each contact, and each
   * array inside one (tags, members), is new. A held list is reused across
   * listener events until contactsRev moves, so an edit the host abandons must
   * not land in it. Deliberately not structuredClone: that would turn a
   * Firestore Timestamp in a contact into a plain object without toDate().
   */
  function copyList(list) {
    return (Array.isArray(list) ? list : []).map((c) => {
      if (!c || typeof c !== 'object') return c;
      const out = { ...c };
      Object.keys(out).forEach((k) => { if (Array.isArray(out[k])) out[k] = out[k].slice(); });
      return out;
    });
  }

  function toChunks(contacts) {
    const chunks = [];
    for (let i = 0; i < contacts.length; i += CHUNK_SIZE) {
      chunks.push({ index: chunks.length, items: contacts.slice(i, i + CHUNK_SIZE) });
    }
    return chunks;
  }

  /**
   * Write the guest list plus any other parent fields (`also`) in one batch.
   * `create: true` uses set() on a fresh ref (pass `db.collection(...).doc()`
   * for an auto id, the way `.add()` did). Returns the parent fields written.
   */
  async function save(db, campaignRef, contacts, opts) {
    const o = opts || {};
    const also = o.also || {};
    const list = Array.isArray(contacts) ? contacts : [];
    const batch = db.batch();
    let parentFields;
    if (WRITE_STORAGE === STORAGE_CHUNKS) {
      const chunks = toChunks(list);
      const col = chunksRef(db, campaignRef.id);
      const keep = new Set(chunks.map((c) => String(c.index)));
      if (!o.create) {
        const existing = await col.get();
        existing.forEach((d) => { if (!keep.has(d.id)) batch.delete(d.ref); });
      }
      chunks.forEach((c) => batch.set(col.doc(String(c.index)), c));
      parentFields = { ...also, [STORAGE_FIELD]: STORAGE_CHUNKS, [CHUNKS_FIELD]: chunks.length, ...versionBump(), [REV_FIELD]: firebase.firestore.FieldValue.increment(1) };
      if (!o.create) parentFields.contacts = firebase.firestore.FieldValue.delete();
    } else {
      parentFields = { ...also, contacts: list, ...versionBump() };
    }
    if (o.create) batch.set(campaignRef, parentFields);
    else batch.update(campaignRef, parentFields);
    await batch.commit();
    return parentFields;
  }

  /**
   * For callers that already hold a batch (the wizard commits the parent and
   * its reminder children together): stage the chunk writes on `batch` and
   * return the parent fields to spread into the parent's set()/update().
   * Inline mode returns `{ contacts }` so the caller's code is shape-agnostic.
   */
  async function stage(db, batch, campaignRef, contacts, opts) {
    const o = opts || {};
    const list = Array.isArray(contacts) ? contacts : [];
    if (WRITE_STORAGE !== STORAGE_CHUNKS) return { contacts: list, ...versionBump() };
    const chunks = toChunks(list);
    const col = chunksRef(db, campaignRef.id);
    const keep = new Set(chunks.map((c) => String(c.index)));
    if (!o.create) {
      const existing = await col.get();
      existing.forEach((d) => { if (!keep.has(d.id)) batch.delete(d.ref); });
    }
    chunks.forEach((c) => batch.set(col.doc(String(c.index)), c));
    const fields = { [STORAGE_FIELD]: STORAGE_CHUNKS, [CHUNKS_FIELD]: chunks.length, ...versionBump(), [REV_FIELD]: firebase.firestore.FieldValue.increment(1) };
    if (!o.create) fields.contacts = firebase.firestore.FieldValue.delete();
    return fields;
  }

  /** Delete the chunk docs; call wherever a campaign doc is deleted. */
  async function remove(db, campaignId) {
    const snap = await chunksRef(db, campaignId).get();
    if (snap.empty) return 0;
    const batch = db.batch();
    snap.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    return snap.size;
  }

  window.CampaignContacts = {
    SUBCOLLECTION, STORAGE_FIELD, CHUNKS_FIELD, VERSION_FIELD, REV_FIELD, STORAGE_CHUNKS, CHUNK_SIZE, WRITE_STORAGE,
    hasInline, hasChunked, contactsKey, canReuse, load, hydrate, hydrateAll, copyList, toChunks, save, stage, remove
  };
})();
